import { ThemeEvents, DiscountUpdateEvent } from "./events.js";

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.appliedDiscounts = new Set();

    this.apply = this.apply.bind(this);
    this.toggle = this.toggle.bind(this);
    this.clearAllDiscounts = this.clearAllDiscounts.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.open = this.querySelector(".insert-code");
    this.error = this.querySelector(".discount-error");
    this.discountsList = this.querySelector(".applied-discounts-list");

    this.form?.addEventListener("submit", this.apply);
    this.open?.addEventListener("click", this.toggle);

    this.querySelectorAll(".remove-discount, [data-remove-discount]").forEach((button) => {
      button.addEventListener("click", this.clearAllDiscounts);
    });

    this.clearDiscountInput();
    this.loadSavedDiscounts();
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.apply);
    this.open?.removeEventListener("click", this.toggle);

    this.querySelectorAll(".remove-discount, [data-remove-discount]").forEach((button) => {
      button.removeEventListener("click", this.clearAllDiscounts);
    });
  }

  getRoot() {
    return window.Shopify?.routes?.root || window.routes?.root || "/";
  }

  setError(message = "") {
    if (!this.error) return;

    this.error.textContent = message;
    this.error.hidden = !message;
  }

  setButtonLoading(button, isLoading, loadingText = "Aplicando...") {
    if (!button) return;

    if (isLoading) {
      if (!button.dataset.originalContent) {
        button.dataset.originalContent = button.innerHTML;
      }

      button.disabled = true;
      button.classList.add("is-loading");
      button.setAttribute("aria-busy", "true");

      const spinner = button.querySelector(".form-loading, .loading-spinner");

      if (spinner) {
        spinner.classList.remove("hidden");
      } else if (button.classList.contains("remove-single-discount")) {
        button.innerHTML = `<span class="button-mini-spinner" aria-hidden="true"></span>`;
      } else if (loadingText) {
        button.innerHTML = `<span>${loadingText}</span>`;
      }
    } else {
      button.disabled = false;
      button.classList.remove("is-loading");
      button.removeAttribute("aria-busy");

      if (button.dataset.originalContent) {
        button.innerHTML = button.dataset.originalContent;
        delete button.dataset.originalContent;
      }

      const spinner = button.querySelector(".form-loading, .loading-spinner");
      if (spinner) {
        spinner.classList.add("hidden");
      }
    }
  }

  clearDiscountInput() {
    const discountInput = this.form?.querySelector('[name="discount"]');
    if (discountInput) discountInput.value = "";
  }

  async getCart() {
    const response = await fetch(`${this.getRoot()}cart.js`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error("Não foi possível buscar o carrinho atualizado.");
    }

    return response.json();
  }

  async updateDiscount(discountString) {
    const response = await fetch(`${this.getRoot()}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ discount: discountString || "" }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || (data.status && data.status !== 200)) {
      throw new Error(data.description || data.message || "Não foi possível atualizar o cupom.");
    }

    return data;
  }

  updateCartCount(cart) {
    if (!cart) return;

    document.querySelectorAll("[data-cart-count]").forEach((element) => {
      element.textContent = cart.item_count;
    });

    document.querySelectorAll("[data-cart-text]").forEach((element) => {
      if (!window.cartStrings) return;
      element.textContent = cart.item_count === 1 ? window.cartStrings.item : window.cartStrings.items;
    });
  }

reinitCartShippingSoon() {
  const delays = [100, 300, 700, 1200];

  delays.forEach((delay) => {
    window.setTimeout(() => {
      if (typeof window.reinitCartShipping === "function") {
        window.reinitCartShipping();
      }
    }, delay);
  });
}

  async refreshHaloSideCart(triggerButton, cart) {
    this.updateCartCount(cart);

    document.dispatchEvent(new DiscountUpdateEvent(cart, this.dataset.sectionId));
    document.dispatchEvent(new CustomEvent("cart-update", { detail: cart }));

    document.querySelectorAll("cart-progressbar").forEach((progressbar) => {
      if (typeof progressbar.updateProgressBar === "function") {
        progressbar.updateProgressBar(cart);
      }
    });

    if (typeof window.forceRefreshSidebarCart === "function") {
      window.forceRefreshSidebarCart(triggerButton, cart);
      this.reinitCartShippingSoon();
      return;
    }

    const cartWrapper =
      document.querySelector("#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper") ||
      document.querySelector("#halo-cart-sidebar .previewCart-wrapper") ||
      document.querySelector(".previewCart-wrapper");

    if (!cartWrapper) {
      if (typeof window.updatecart === "function") {
        window.updatecart(cart);
      }

      this.reinitCartShippingSoon();
      return;
    }

    cartWrapper.classList.add("is-loading");

    try {
      const response = await fetch(`${this.getRoot()}cart?view=ajax_side_cart&_=${Date.now()}`, {
        method: "GET",
        headers: { Accept: "text/html" },
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Não foi possível atualizar o carrinho lateral.");
      }

      const html = await response.text();
      const tempWrapper = document.createElement("div");
      tempWrapper.innerHTML = html;

      [".previewCartList", ".previewCartInfo", ".previewCartTool", ".popup-toolDowns"].forEach((selector) => {
        const currentElement = cartWrapper.querySelector(selector);
        const newElement = tempWrapper.querySelector(selector);

        if (currentElement && newElement) {
          currentElement.replaceWith(newElement);
        }
      });

      if (typeof halo !== "undefined") {
        if (typeof halo.dispatchChangeForShippingMessage === "function") {
          halo.dispatchChangeForShippingMessage();
        }

        if (typeof halo.updateGiftWrapper === "function") {
          halo.updateGiftWrapper();
        }
      }

      if (typeof window.updatecart === "function") {
        window.updatecart(cart);
      }

      this.reinitCartShippingSoon();
    } catch (error) {
      console.error("[CartDiscount] Erro ao atualizar o side cart:", error);
    } finally {
      cartWrapper.classList.remove("is-loading");
      cartWrapper.querySelectorAll(".loading-overlay--custom").forEach((overlay) => overlay.remove());
    }
  }

  async loadSavedDiscounts() {
    try {
      const cart = await this.getCart();
      const currentDiscounts = new Set();

      if (Array.isArray(cart.discount_codes)) {
        cart.discount_codes.forEach((discount) => {
          if (discount.applicable && discount.code) {
            currentDiscounts.add(discount.code);
          }
        });
      }

      this.appliedDiscounts = currentDiscounts;
      this.saveDiscounts();
      this.renderDiscountsList(cart);
    } catch (error) {
      const saved = localStorage.getItem("appliedDiscounts");
      if (!saved) return;

      try {
        this.appliedDiscounts = new Set(JSON.parse(saved));
        this.renderDiscountsList();
      } catch (_) {
        localStorage.removeItem("appliedDiscounts");
      }
    }
  }

  saveDiscounts() {
    localStorage.setItem("appliedDiscounts", JSON.stringify([...this.appliedDiscounts]));
  }

  toggle(e) {
    e.preventDefault();

    const isOpen = this.dataset.open === "opened";
    this.dataset.open = isOpen ? "closed" : "opened";

    this.dispatchEvent(
      new CustomEvent(ThemeEvents.DISCOUNT_TOGGLE, {
        detail: { open: !isOpen, sectionId: this.dataset.sectionId },
      })
    );

    this.clearDiscountInput();
    this.setError("");

    const input = this.form?.querySelector('[name="discount"]');
    if (!isOpen && input) {
      setTimeout(() => input.focus(), 50);
    }
  }

  apply = async (e) => {
    e.preventDefault();

    const discountInput = this.form?.querySelector('[name="discount"]');
    const discountCode = discountInput?.value?.trim();
    const submitButton = this.form?.querySelector('button[type="submit"], input[type="submit"]');

    this.setError("");

    if (!discountCode) {
      this.setError("Por favor, insira um código de desconto.");
      discountInput?.focus();
      return;
    }

    if (this.appliedDiscounts.has(discountCode)) {
      this.setError("Este código de desconto já foi aplicado.");
      discountInput?.focus();
      return;
    }

    this.setButtonLoading(submitButton, true, "Aplicando...");

    try {
      const allDiscounts = [...this.appliedDiscounts, discountCode];
      const discountString = allDiscounts.join(",");

      await this.updateDiscount(discountString);
      const cart = await this.getCart();

      const appliedCodes = new Set();
      if (Array.isArray(cart.discount_codes)) {
        cart.discount_codes.forEach((discount) => {
          if (discount.applicable && discount.code) {
            appliedCodes.add(discount.code);
          }
        });
      }

      if (appliedCodes.size === 0 && cart.total_discount <= 0) {
        throw new Error("Cupom inválido ou não aplicável para este carrinho.");
      }

      this.appliedDiscounts = appliedCodes.size ? appliedCodes : new Set(allDiscounts);
      this.saveDiscounts();
      this.clearDiscountInput();
      this.renderDiscountsList(cart);
      await this.refreshHaloSideCart(submitButton, cart);
    } catch (error) {
      console.error("[CartDiscount] Erro ao aplicar desconto:", error);
      this.setError(error.message || "Erro ao aplicar desconto. Tente novamente.");
    } finally {
      this.setButtonLoading(submitButton, false);
    }
  };

  async renderDiscountsList(cartData = null) {
    if (!this.discountsList) return;

    if (!cartData) {
      try {
        cartData = await this.getCart();
      } catch (_) {
        cartData = null;
      }
    }

    const discountsArray = [...this.appliedDiscounts];

    if (discountsArray.length === 0) {
      this.discountsList.innerHTML = "";
      this.discountsList.hidden = true;
      return;
    }

    this.discountsList.innerHTML = `
      <div class="applied-discounts-container">
        <div class="discount-tags">
          ${discountsArray
            .map((code) => {
              let isApplicable = true;

              if (cartData && Array.isArray(cartData.discount_codes)) {
                const discountInfo = cartData.discount_codes.find((discount) => discount.code === code);
                isApplicable = discountInfo ? discountInfo.applicable : true;
              }

              return `
                <div class="discount-tag ${!isApplicable ? "discount-tag--not-applicable" : ""}">
                  <span class="discount-tag__icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.25 4.5V7.13C2.25 7.53 2.41 7.91 2.69 8.19L9.81 15.31C10.4 15.9 11.35 15.9 11.94 15.31L15.31 11.94C15.9 11.35 15.9 10.4 15.31 9.81L8.19 2.69C7.91 2.41 7.53 2.25 7.13 2.25H4.5C3.26 2.25 2.25 3.26 2.25 4.5Z" stroke="currentColor" stroke-width="1.4"/>
                      <circle cx="5.8" cy="5.8" r="1.05" fill="currentColor"/>
                    </svg>
                  </span>

                  <span class="discount-tag__content">
                    <span class="discount-tag__label">Cupom aplicado</span>
                    <span class="discount-tag__code">${code}</span>
                    ${!isApplicable ? '<span class="discount-tag__status">Não aplicável</span>' : ""}
                  </span>

                  <button type="button" class="remove-single-discount" data-discount="${code}" aria-label="Remover cupom ${code}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="currentColor"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="currentColor"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="currentColor"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="currentColor"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="currentColor"></path>
                    </svg>
                  </button>
                </div>
              `;
            })
            .join("")}
        </div>
      </div>
    `;

    this.discountsList.hidden = false;

    this.discountsList.querySelectorAll(".remove-single-discount").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        const discountToRemove = event.currentTarget.dataset.discount;
        if (discountToRemove) {
          this.removeSingleDiscount(discountToRemove, event.currentTarget);
        }
      });
    });
  }

  removeSingleDiscount = async (discountCode, buttonElement = null) => {
    this.setError("");
    this.setButtonLoading(buttonElement, true, "");

    try {
      this.appliedDiscounts.delete(discountCode);
      const remainingDiscounts = [...this.appliedDiscounts];

      await this.updateDiscount(remainingDiscounts.join(","));
      const cart = await this.getCart();

      const currentDiscounts = new Set();
      if (Array.isArray(cart.discount_codes)) {
        cart.discount_codes.forEach((discount) => {
          if (discount.applicable && discount.code) {
            currentDiscounts.add(discount.code);
          }
        });
      }

      this.appliedDiscounts = currentDiscounts;
      this.saveDiscounts();
      this.renderDiscountsList(cart);
      await this.refreshHaloSideCart(buttonElement, cart);
    } catch (error) {
      console.error("[CartDiscount] Erro ao remover desconto:", error);
      this.setError(error.message || "Erro ao remover desconto. Tente novamente.");
    } finally {
      this.setButtonLoading(buttonElement, false);
    }
  };

  clearAllDiscounts = async (eventOrButton = null) => {
    const buttonElement = eventOrButton?.currentTarget || eventOrButton;

    if (eventOrButton?.preventDefault) {
      eventOrButton.preventDefault();
    }

    this.setError("");
    this.setButtonLoading(buttonElement, true, "Removendo...");

    try {
      await this.updateDiscount("");
      const cart = await this.getCart();

      this.appliedDiscounts.clear();
      this.saveDiscounts();
      this.renderDiscountsList(cart);
      await this.refreshHaloSideCart(buttonElement, cart);
    } catch (error) {
      console.error("[CartDiscount] Erro ao remover cupons:", error);
      this.setError(error.message || "Erro ao remover cupom. Tente novamente.");
    } finally {
      this.setButtonLoading(buttonElement, false);
    }
  };
}

if (!customElements.get("cart-discount-component")) {
  customElements.define("cart-discount-component", CartDiscount);
}

class CartShipping extends HTMLElement {
  constructor() {
    super();

    this.onToggleClick = this.toggle.bind(this);
    this.onApplyClick = this.apply.bind(this);
    this.onFormSubmit = this.handleSubmit.bind(this);
    this.onCepKeydown = this.handleCepKeydown.bind(this);
    this.onPopupOverlayClick = this.handlePopupOverlayClick.bind(this);
    this.onPopupCloseClick = this.handlePopupCloseClick.bind(this);
    this.onClearShippingClick = this.clearShipping.bind(this);
    this._calcBtn = null;
    this._cepInput = null;
    this._popup = null;
    this._popupCloseBtn = null;
    this._refazerBtn = null;
  }

  fmtBRL(v) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(v) || 0);
  }

  normalizeDate(dateLike) {
    const d = new Date(dateLike);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  addBusinessDays(startDate, businessDays, holidays = []) {
    const date = this.normalizeDate(startDate);
    let added = 0;
    const totalDays = Number(businessDays) || 0;

    while (added < totalDays) {
      date.setDate(date.getDate() + 1);

      const day = date.getDay();
      const isWeekend = day === 0 || day === 6;
      const isHoliday = this.isHoliday(date, holidays);

      if (!isWeekend && !isHoliday) {
        added++;
      }
    }

    return date;
  }

  formatDeadlineText(days, isBusiness = true) {
    const numericDays = Number(days) || 0;
    if (numericDays === 1) {
      return isBusiness ? "Em até 1 dia útil" : "Em até 1 dia";
    }
    return isBusiness
      ? `Em até ${numericDays} dias úteis`
      : `Em até ${numericDays} dias`;
  }

  deliveryHtml(range) {
    if (!range) return "";

    if (typeof range === "string") {
      const matchBusiness = range.match(/(\d+)\s*dias?\s*úteis/i);
      if (matchBusiness) {
        return this.formatDeadlineText(parseInt(matchBusiness[1], 10), true);
      }

      const matchDays = range.match(/(\d+)\s*dias?/i);
      if (matchDays) {
        return this.formatDeadlineText(parseInt(matchDays[1], 10), false);
      }

      return range;
    }

    const min = range.min ?? range.minimum ?? range.from ?? range[0] ?? null;
    const max = range.max ?? range.maximum ?? range.to ?? range[1] ?? null;
    const unit = String(
      range.unit || range.units || range.type || ""
    ).toLowerCase();

    if (typeof min === "number" || typeof max === "number") {
      const same = (min ?? max) === (max ?? min);
      const value = same ? (min ?? max) : (max ?? min);
      const isBusiness = unit.includes("business") || unit.includes("útil");

      return this.formatDeadlineText(value, isBusiness);
    }

    return "";
  }

  connectedCallback() {
    this.form =
      this.querySelector(".shipping-component-form") ||
      this.querySelector(".shipping-form") ||
      this.querySelector(".frete-form") ||
      this.querySelector("form") ||
      this.querySelector("div[role='form']");

    this.open = this.querySelector(".insert-cep");
    this.error = this.querySelector(".shipping-component-error");

    this.ensurePopup();

    if (this.form) {
      this.form.addEventListener("submit", this.onFormSubmit, true);
      this._cepInput = this.form.querySelector('[name="shipping-component"]');
      this._cepInput?.addEventListener("keydown", this.onCepKeydown, true);
      this._calcBtn =
        this.form.querySelector('button[type="submit"]') ||
        this.form.querySelector('input[type="submit"]');
    }

    this.open?.addEventListener("click", this.onToggleClick);

    const result = this.querySelector(".selected-shipping-components");
    if (result) {
      const wrapper = result.querySelector(".shipping-wrapper");
      if (wrapper) wrapper.innerHTML = "";
      result.classList.add("hidden");
    }
  }

  disconnectedCallback() {
    this.open?.removeEventListener("click", this.onToggleClick);
    this.form?.removeEventListener("submit", this.onFormSubmit, true);
    this._cepInput?.removeEventListener("keydown", this.onCepKeydown, true);
    this.teardownPopup();
    this.teardownRefazer();
  }

  ensurePopup() {
    let popup = document.getElementById("shipping-popup");
    if (!popup) {
      popup = document.createElement("div");
      popup.id = "shipping-popup";
      popup.style.position = "fixed";
      popup.style.inset = "0";
      popup.style.width = "100vw";
      popup.style.height = "100vh";
      popup.style.background = "rgba(0,0,0,0.5)";
      popup.style.display = "none";
      popup.style.justifyContent = "center";
      popup.style.alignItems = "center";
      popup.style.zIndex = "9999";
      popup.innerHTML = `
        <div
          id="shipping-popup-content"
          style="
            background:#fff;
            max-width:420px;
            width:90vw;
            max-height:80vh;
            overflow:auto;
            padding:24px 16px 16px;
            border-radius:8px;
            position:relative;
            box-shadow:0 2px 16px rgba(0,0,0,0.2);
          "
        >
          <button
            id="close-shipping-popup"
            type="button"
            style="
              position:absolute;
              top:8px;
              right:8px;
              background:none;
              border:none;
              font-size:1.5rem;
              cursor:pointer;
            "
          >&times;</button>

          <h3 style="margin-top:0;">Opções de Frete</h3>
          <div id="shipping-popup-list"></div>
        </div>
      `;
      document.body.appendChild(popup);
    }

    this._popup = popup;
    this._popupCloseBtn = document.getElementById("close-shipping-popup");

    this._popup.removeEventListener("click", this.onPopupOverlayClick);
    this._popup.addEventListener("click", this.onPopupOverlayClick);

    this._popupCloseBtn?.removeEventListener("click", this.onPopupCloseClick);
    this._popupCloseBtn?.addEventListener("click", this.onPopupCloseClick);
  }

  teardownPopup() {
    this._popup?.removeEventListener("click", this.onPopupOverlayClick);
    this._popupCloseBtn?.removeEventListener("click", this.onPopupCloseClick);
  }

  handlePopupOverlayClick(e) {
    if (e.target === this._popup) {
      this.hidePopup();
    }
  }

  handlePopupCloseClick() {
    this.hidePopup();
  }

  showPopup() {
    if (this._popup) {
      this._popup.style.display = "flex";
      document.body.style.overflow = "hidden";
    }
  }

  hidePopup() {
    if (this._popup) {
      this._popup.style.display = "none";
      document.body.style.overflow = "";
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    this.apply();
    return false;
  }

  handleCepKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      this.apply();
      return false;
    }
  }

  setError(message = "") {
    if (!this.error) return;
    if (message) {
      this.error.textContent = message;
      this.error.hidden = false;
    } else {
      this.error.textContent = "";
      this.error.hidden = true;
    }
  }

  toggle(e) {
    e.preventDefault();

    const isOpen = this.dataset.open === "opened";
    this.dataset.open = isOpen ? "closed" : "opened";

    if (isOpen) {
      if (this._cepInput) this._cepInput.value = "";
      this.setError("");
    }

    if (this.open) {
      this.open.textContent = isOpen ? "Calcule seu frete" : "Fechar";
    }
  }

  getCookie(name) {
    var value = "; " + document.cookie;
    var parts = value.split("; " + name + "=");
    if (parts.length == 2) return parts.pop().split(";").shift();
  }

  updateCartCookie(a) {
    var date = new Date();
    date.setTime(date.getTime() + 14 * 86400000);
    var expires = "; expires=" + date.toGMTString();
    document.cookie = "cart=" + a + expires + "; path=/";
  }

  async apply(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    }

    const raw = this._cepInput?.value || "";
    const zip = raw.replace(/\D/g, "");

    if (zip.length !== 8) {
      this.setError("Informe um CEP válido (8 dígitos).");
      this.renderPopupList([]);
      this.showPopup();
      return;
    }

    let province = null;

    try {
      const viaCepRes = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
      const viaCepData = await viaCepRes.json();
      if (viaCepData && viaCepData.uf) {
        province = viaCepData.uf;
      }
    } catch (err) {
      province = null;
    }

    if (!province) {
      this.setError(
        "Não foi possível identificar o estado (UF) pelo CEP. Informe um CEP válido."
      );
      this.renderPopupList([]);
      this.showPopup();
      return;
    }

    const spinner = this.form?.querySelector(".form-loading");
    spinner?.classList.remove("hidden");
    this.setError("");

    if (this._calcBtn) {
      this._calcBtn.disabled = true;
    }

    try {
      const root = window.Shopify?.routes?.root || "/";

      let currCartCookie, tempCookieValue, fakeCookieValue;

      if (this.classList.contains("cart-shipping-pdp")) {
        currCartCookie = this.getCookie("cart");
        tempCookieValue = "temp-cart-cookie___" + Date.now();
        fakeCookieValue = "fake-cart-cookie___" + Date.now();

        if (!currCartCookie) {
          this.updateCartCookie(tempCookieValue);
          currCartCookie = this.getCookie("cart");
        }

        this.updateCartCookie(fakeCookieValue);

        let formData = { items: [] };

        if (document.body.classList.contains("template-suffix-kit")) {
          formData.items = [];
          const ids = Array.from(
            document.querySelectorAll("product-kit-item")
          ).map((item) => item.getAttribute("variant-id"));

          ids.forEach((id) => formData.items.push({ id, quantity: 1 }));
        } else {
          const qtInput = document.querySelector(
            "product-form .quantity__input"
          );
          const idInput = document.querySelector(
            'product-form input[name="id"]'
          );

          formData = {
            items: [
              {
                id: idInput.value,
                quantity: qtInput.value,
              },
            ],
          };
        }

        await fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        })
          .then((response) => {
            return response.json();
          })
          .catch((error) => {
            console.error("Error:", error);
            this.updateCartCookie(currCartCookie);
          });
      }

      const prepareRes = await fetch(
        `${root}cart/prepare_shipping_rates.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            shipping_address: {
              zip,
              country: "Brazil",
              province,
            },
          }),
        }
      );

      if (!prepareRes.ok && prepareRes.status !== 202) {
        let errData = null;
        let errText = null;

        try {
          errData = await prepareRes.json();
        } catch (_) {
          try {
            errText = await prepareRes.text();
          } catch (__) {
            errText = null;
          }
        }

        throw new Error(
          errData?.error ||
            errData?.errors ||
            errText ||
            `Não foi possível iniciar o cálculo de frete. [${prepareRes.status}]`
        );
      }

      const params = new URLSearchParams({
        "shipping_address[zip]": zip,
        "shipping_address[country]": "Brazil",
      });

      const rates = await this.pollRates(
        `${root}cart/async_shipping_rates.json?${params.toString()}`
      );

      if (!Array.isArray(rates) || !rates.length) {
        this.setError("Nenhuma opção de frete para este CEP.");
        this.renderPopupList([]);
        this.showPopup();

        if (this.classList.contains("cart-shipping-pdp")) {
          this.updateCartCookie(currCartCookie);
        }

        return;
      }

      rates.sort((a, b) => Number(a.price) - Number(b.price));

      if (this.classList.contains("cart-shipping-pdp")) {
        this.updateCartCookie(currCartCookie);
        this.renderList(rates);
      } else {
        this.renderPopupList(rates);
        this.showPopup();
      }
    } catch (err) {
      this.setError(
        err?.message || "Erro ao consultar frete. Tente novamente."
      );
      this.renderPopupList([]);
      this.showPopup();
    } finally {
      spinner?.classList.add("hidden");

      if (this._calcBtn) {
        this._calcBtn.disabled = false;
      }
    }
  }

  async pollRates(url, { tries = 20, delay = 700 } = {}) {
    for (let i = 0; i < tries; i++) {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      let data = null;

      try {
        data = await res.json();
      } catch (_) {
        data = null;
      }

      if (Array.isArray(data?.shipping_rates)) {
        return data.shipping_rates;
      }

      if (Array.isArray(data)) {
        return data;
      }

      if (data?.error) {
        throw new Error(
          Array.isArray(data.error) ? data.error.join(", ") : String(data.error)
        );
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    return [];
  }

  renderList(rates) {
    const result = this.querySelector(".selected-shipping-components");

    if (result) {
      result.classList.remove("hidden");
      result.style.display = "";

      const wrapper = result.querySelector(".shipping-wrapper");
      if (wrapper) wrapper.innerHTML = "";

      wrapper.innerHTML = `
        ${rates
          .map((r) => {
            return `
              <li>
                <span class="shipping-name">${r.name}</span>
                <span class="shipping-price">${Number(r.price) === 0 ? "Grátis" : this.fmtBRL(r.price)}</span>
                <span class="shipping-date">chega em ${r.delivery_days[0] == 1 ? `1 dia útil` : `${r.delivery_days[0]} dias úteis`}</span>
              </li>
            `;
          })
          .join("")}
      `;
    }
  }

  renderPopupList(rates) {
    const popupList = document.getElementById("shipping-popup-list");
    if (!popupList) return;

    if (!Array.isArray(rates) || !rates.length) {
      popupList.innerHTML = `
        <div style="color:#e00;text-align:center;padding:16px;">
          Nenhuma opção de frete disponível para este CEP.
        </div>
      `;
      return;
    }

    let selectedIdx = 0;

    popupList.innerHTML = `
      <ul style="list-style:none;padding:0;margin:0;">
        ${rates
          .map((r, i) => {
            return `
              <li>
                <label>
                  <input type="radio" name="shipping-option" value="${i}">
                  <span class="shipping-name">${r.name}</span>
                  <span class="shipping-price">${Number(r.price) === 0 ? "Grátis" : this.fmtBRL(r.price)}</span>
                  <span class="shipping-date">chega em ${r.delivery_days[0] == 1 ? `1 dia útil` : `${r.delivery_days[0]} dias úteis`}</span>
                </label>
              </li>
            `;
          })
          .join("")}
      </ul>

      <button
        id="select-shipping-popup-btn"
        type="button"
        style="
          width:100%;
          background:#232323;
          color:#fff;
          padding:10px 0;
          border:none;
          border-radius:4px;
          font-size:1.1em;
          cursor:pointer;
        "
      >
        Selecionar Frete
      </button>
    `;

    const radios = popupList.querySelectorAll('input[name="shipping-option"]');
    radios.forEach((input) => {
      input.addEventListener("change", (ev) => {
        selectedIdx = Number(ev.target.value);
      });
    });

    const selectBtn = popupList.querySelector("#select-shipping-popup-btn");
    selectBtn?.addEventListener("click", () => {
      const selected = rates[selectedIdx];

      if (!selected) {
        selectBtn.textContent = "Selecione uma opção";
        setTimeout(() => {
          selectBtn.textContent = "Selecionar Frete";
        }, 1200);
        return;
      }

      this.hidePopup();
      this.renderSelectedOnAllComponents(selected);

      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    });
  }

  renderSelectedOnAllComponents(selected) {
    const allComponents = document.querySelectorAll("cart-shipping-component");

    allComponents.forEach((component) => {
      if (typeof component.renderSelected === "function") {
        component.renderSelected(selected);
      }
    });
  }

  renderSelected(selected) {
    const listBox = this.querySelector(".selected-shipping-components");
    if (!listBox) return;

    const wrapper = listBox.querySelector(".shipping-wrapper");
    let resume = listBox.querySelector(".shipping-resume");

    if (!resume) {
      resume = document.createElement("div");
      resume.className = "shipping-resume list-unstyled";
      listBox.appendChild(resume);
    }

    if (wrapper) wrapper.style.display = "none";

    listBox.classList.remove("hidden");
    this.classList.add("shipping-component-applied");

    const prazo = selected.delivery_range
      ? this.deliveryHtml(selected.delivery_range)
      : selected.delivery_days && selected.delivery_days.length
        ? this.formatDeadlineText(selected.delivery_days[0], true)
        : "";

    resume.innerHTML = `
      <li class="shipping-component-item">
        <span class="shipping-selected-wrapper">
          <span>${selected.name || "Frete"} ${prazo ? `(${prazo})` : ""}</span>
        </span>
        <span>${Number(selected.price) === 0 ? "Grátis" : this.fmtBRL(selected.price)}</span>
        <button type="button" class="remove-shipping-component" aria-label="Refazer cálculo">&#10005;</button>
      </li>
    `;

    this.setAttribute("data-open", "closed");

    if (this.open) {
      this.open.textContent = "Calcule seu frete";
    }

    this.teardownRefazer();
    this._refazerBtn = resume.querySelector(".remove-shipping-component");
    this._refazerBtn?.addEventListener("click", this.onClearShippingClick);
  }

  teardownRefazer() {
    if (this._refazerBtn) {
      this._refazerBtn.removeEventListener("click", this.onClearShippingClick);
      this._refazerBtn = null;
    }
  }

  clearShipping() {
    const allComponents = document.querySelectorAll("cart-shipping-component");

    allComponents.forEach((component) => {
      const listBox = component.querySelector(".selected-shipping-components");
      const wrapper = listBox?.querySelector(".shipping-wrapper");
      const resume = listBox?.querySelector(".shipping-resume");

      if (resume) resume.remove();
      if (wrapper) wrapper.style.display = "";

      listBox?.classList.add("hidden");
      component.classList.remove("shipping-component-applied");
      component.dataset.open = "opened";

      if (component.open) {
        component.open.textContent = "Fechar";
      }

      if (component._cepInput) {
        component._cepInput.value = "";
      }

      if (typeof component.setError === "function") {
        component.setError("");
      }
    });

    if (this._cepInput) {
      this._cepInput.focus();
    }

    if (window.location.pathname === "/cart") {
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  }
}

if (!customElements.get("cart-shipping-component")) {
  customElements.define("cart-shipping-component", CartShipping);
}

window.reinitCartShipping = function () {
  document.querySelectorAll("cart-shipping-component").forEach((component) => {
    if (!component || typeof component.toggle !== "function") return;

    if (component.open && component.onToggleClick) {
      component.open.removeEventListener("click", component.onToggleClick);
    }

    if (component.form && component.onFormSubmit) {
      component.form.removeEventListener("submit", component.onFormSubmit, true);
    }

    if (component._cepInput && component.onCepKeydown) {
      component._cepInput.removeEventListener("keydown", component.onCepKeydown, true);
    }

    component.form =
      component.querySelector(".shipping-component-form") ||
      component.querySelector(".shipping-form") ||
      component.querySelector(".frete-form") ||
      component.querySelector("form") ||
      component.querySelector("div[role='form']");

    component.open = component.querySelector(".insert-cep");
    component.error = component.querySelector(".shipping-component-error");

    if (component.form) {
      component.form.addEventListener("submit", component.onFormSubmit, true);

      component._cepInput = component.form.querySelector('[name="shipping-component"]');

      if (component._cepInput) {
        component._cepInput.removeEventListener("keydown", component.onCepKeydown, true);
        component._cepInput.addEventListener("keydown", component.onCepKeydown, true);
      }

      component._calcBtn =
        component.form.querySelector('button[type="submit"]') ||
        component.form.querySelector('input[type="submit"]');
    }

    if (component.open) {
      component.open.removeEventListener("click", component.onToggleClick);
      component.open.addEventListener("click", component.onToggleClick);
    }

    if (typeof component.ensurePopup === "function") {
      component.ensurePopup();
    }
  });
};
